import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { useElectronicCardInfo, usePhysicalCardInfo } from '@/hooks'
import AddCard from './add-card'
import ElectronicCard from './electronic-card'
import PhysicalCard from './physical-card'
import PhysicalCardStatus from './physical-card/physical-card-status'
import { Loading } from 'react-vant'
import PageLayout from "@/components/PageLayout"
import Tabs from '@/components/Tabs'
import { type Item } from '@/components/Tabs/index.type'
import { CardType } from './index.type'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

export default React.memo(function Card() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // 虚拟卡状态
  const { hasCard: hasElectronicCard, loading: electronicCardLoading, actions: electronicCardActions } = useElectronicCardInfo()
  
  // 实体卡状态
  const { hasCard: hasPhysicalCard, loading: physicalCardLoading, cardInfo: physicalCardInfo, actions: physicalCardActions } = usePhysicalCardInfo()
  
  // 从 URL 参数读取卡片类型，如果没有则默认为电子卡
  const cardTypeFromUrl = searchParams.get('type') as CardType
  const validCardTypes = [CardType.ElectronicCard, CardType.PhysicalCard]
  const initialCardType = cardTypeFromUrl && validCardTypes.includes(cardTypeFromUrl) 
    ? cardTypeFromUrl 
    : CardType.ElectronicCard
  
  const [selectedCardType, setSelectedCardType] = useState<CardType>(initialCardType)
  
  // 同步 URL 参数和状态
  useEffect(() => {
    const urlType = searchParams.get('type')
    if (urlType !== selectedCardType) {
      setSearchParams({ type: selectedCardType }, { replace: true })
    }
  }, [selectedCardType, searchParams, setSearchParams])
  // 追踪用户是否点击了添加新卡（电子卡支持多卡）
  const [isAddingElectronicCard, setIsAddingElectronicCard] = useState(false)

  // 刷新虚拟卡信息
  useEffect(() => {
    electronicCardActions.refreshCardInfo()
    physicalCardActions.refreshCardInfo()
  }, [])

  // 判断实体卡状态
  // - null: 未申请
  // - 'pending': 已申请但未激活（申核中、制卡中、邮寄中、激活中）
  // - 'active': 已激活
  const physicalCardStatus = useMemo<'pending' | 'active' | null>(() => {
    if (!hasPhysicalCard || !physicalCardInfo) {
      return null // 未申请
    }
    
    // 根据实体卡信息判断状态
    // card_status: 0-已注销 1-已激活 2-已冻结 3-已过期 4-已锁定 9-未激活
    const cardStatus = physicalCardInfo.card_status
    if (cardStatus === '1') {
      // 已激活
      return 'active'
    } else if (cardStatus === '9') {
      // 未激活、已注销或状态未知 -> pending（等待激活或处理中）
      return 'pending'
    } else {
      // 其他状态（已冻结、已过期、已锁定）也视为 active（已激活过的卡片）
      return 'active'
    }
  }, [hasPhysicalCard, physicalCardInfo])

  // Tab 数据
  const tabsItems = useMemo<Item<CardType>[]>(() => [
    { type: CardType.ElectronicCard, text: t('电子卡') },
    { type: CardType.PhysicalCard, text: t('实体卡') }
  ], [t])

  // 处理 Tab 切换
  const handleTabChange = useCallback((cardType: CardType) => {
    setSelectedCardType(cardType)
    // 切换 Tab 时重置添加状态
    setIsAddingElectronicCard(false)
  }, [])

  // 处理添加新卡按钮点击
  const handleAddCard = useCallback(() => {
    // 只有电子卡支持多卡，点击添加按钮时显示 AddCard 组件
    if (selectedCardType === CardType.ElectronicCard) {
      setIsAddingElectronicCard(true)
    }
  }, [selectedCardType])

  const renderContent = useMemo(() => {
    // 根据当前选中的卡片类型显示对应的 loading 状态
    const loading = selectedCardType === CardType.ElectronicCard ? electronicCardLoading : physicalCardLoading
    
    if (loading) {
      return (
        <div className="pt-30 flex items-center justify-center">
          <Loading type="ball" />
        </div>
      )
    }

    // 根据选中的卡片类型，判断是否有该类型的卡片
    switch (selectedCardType) {
      case CardType.ElectronicCard:
        // 电子卡支持多卡：
        // 1. 如果用户点击了添加新卡 -> 显示申请页面
        // 2. 如果有电子卡且未点击添加 -> 显示电子卡列表页面
        // 3. 如果没有电子卡 -> 显示申请页面
        if (isAddingElectronicCard) {
          return <AddCard cardType={CardType.ElectronicCard} />
        }
        return hasElectronicCard ? <ElectronicCard /> : <AddCard cardType={CardType.ElectronicCard} />

      case CardType.PhysicalCard:
        // 根据实体卡状态显示不同页面
        if (physicalCardStatus === 'active') {
          // 已激活 -> 显示实体卡主页面
          return <PhysicalCard />
        } else if (physicalCardStatus === 'pending') {
          // 已申请但未激活 -> 显示状态跟踪页面
          return <PhysicalCardStatus />
        } else {
          // 未申请 -> 显示申请页面
          return <AddCard cardType={CardType.PhysicalCard} />
        }

      default:
        return hasElectronicCard ? <ElectronicCard /> : <AddCard cardType={CardType.ElectronicCard} />
    }
  }, [electronicCardLoading, physicalCardLoading, selectedCardType, hasElectronicCard, physicalCardStatus, isAddingElectronicCard])
  return (
    <PageLayout tabbarPage={true} isHeader={false}>
      {/* <Header
        leftChildren={<span>{t('卡片')}</span>}
        rightChildren={
          // 只有电子卡且已有卡片时才显示添加按钮（支持多卡）
          selectedCardType === CardType.ElectronicCard && hasElectronicCard && !isAddingElectronicCard ? (
            <Ripple className="rounded-full">
              <button
                onClick={handleAddCard}
                className="w-10 h-10 flex items-center justify-center bg-[var(--primary-color)] rounded-full"
              >
                <img src="/icon/white-add.svg" alt="" />
              </button>
            </Ripple>
          ) : null
        }
      ></Header> */}
      <div className="px-6 pt-4">
        <Tabs<CardType>
          items={tabsItems}
          defaultSelected={selectedCardType}
          onChange={handleTabChange}
        />
      </div>
      {renderContent}
    </PageLayout>
  )
})